import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { submitIntake } from "@/lib/api";

const AUDIENCES = [
  "Creative Organization",
  "Dance Collective",
  "Sports Organization",
  "Education Brand",
  "Content Creator",
  "Influencer Brand",
  "Events Business",
  "Service Business",
  "Other",
];

const PACKAGES = [
  "Foundation System",
  "Growth System",
  "Business Operating System",
  "Not Sure",
];

const TIMELINES = [
  "ASAP (within 1 week)",
  "Within 2-4 weeks",
  "1-3 months",
  "Flexible",
];

const BUDGETS = [
  "Under $2,500",
  "$2,500 – $5,000",
  "$5,000 – $10,000",
  "$10,000 – $25,000",
  "$25,000+",
  "Open to architecture proposal",
];

const initialForm = {
  full_name: "",
  email: "",
  company: "",
  role: "",
  audience: "",
  package_interest: "",
  timeline: "",
  budget_range: "",
  website: "",
  project_details: "",
  consent_marketing: false,
};

const fieldClass =
  "bg-white/[0.04] border-white/10 text-white placeholder:text-slate-500 rounded-xl h-12 focus-visible:ring-cyan-400/40";

export default function IntakeForm() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submittedId, setSubmittedId] = useState(null);

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e?.target ? e.target.value : e }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.project_details) {
      toast.error("Please complete the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        company: form.company || null,
        role: form.role || null,
        audience: form.audience || null,
        package_interest: form.package_interest || null,
        timeline: form.timeline || null,
        budget_range: form.budget_range || null,
        website: form.website || null,
      };
      const result = await submitIntake(payload);
      setSubmittedId(result.id);
      toast.success("System build request received. We'll reply within one business day.");
      setForm(initialForm);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submittedId) {
    return (
      <div
        data-testid="intake-form-success"
        className="rounded-3xl glass-strong p-10 md:p-14 text-center glow-cyan-soft"
      >
        <div className="inline-flex h-14 w-14 rounded-full bg-cyan-400/10 text-cyan-300 items-center justify-center mb-6 border border-cyan-400/30">
          <CheckCircle2 className="h-7 w-7" />
        </div>
        <h3 className="text-2xl md:text-3xl font-extrabold tracking-tight text-white">
          Your system build request was received.
        </h3>
        <p className="mt-3 text-slate-400 max-w-xl mx-auto">
          Reference ID:{" "}
          <span className="font-mono text-cyan-300">{submittedId}</span>. We'll
          review your requirements and reply with a custom architecture
          proposal, scope agreement, fixed-price invoice, and timeline within
          one business day.
        </p>
        <Button
          data-testid="intake-form-submit-another"
          onClick={() => setSubmittedId(null)}
          variant="outline"
          className="mt-8 rounded-full border-white/10 bg-white/[0.04] hover:bg-white/[0.08] text-white font-semibold"
        >
          Submit another request
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="intake-form"
      className="rounded-3xl glass-strong p-6 md:p-10"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label htmlFor="full_name" className="text-sm font-semibold text-slate-200">
            Full name *
          </Label>
          <Input
            id="full_name"
            data-testid="intake-input-full-name"
            value={form.full_name}
            onChange={update("full_name")}
            placeholder="Jane Doe"
            required
            className={fieldClass}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email" className="text-sm font-semibold text-slate-200">
            Email *
          </Label>
          <Input
            id="email"
            data-testid="intake-input-email"
            type="email"
            value={form.email}
            onChange={update("email")}
            placeholder="you@brand.com"
            required
            className={fieldClass}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="company" className="text-sm font-semibold text-slate-200">
            Brand / Collective
          </Label>
          <Input
            id="company"
            data-testid="intake-input-company"
            value={form.company}
            onChange={update("company")}
            placeholder="Brand or collective name"
            className={fieldClass}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="role" className="text-sm font-semibold text-slate-200">
            Role
          </Label>
          <Input
            id="role"
            data-testid="intake-input-role"
            value={form.role}
            onChange={update("role")}
            placeholder="Founder / Director / Operator"
            className={fieldClass}
          />
        </div>
        <div className="space-y-2">
          <Label className="text-sm font-semibold text-slate-200">
            We are
          </Label>
          <Select value={form.audience} onValueChange={(v) => update("audience")(v)}>
            <SelectTrigger data-testid="intake-select-audience" className={fieldClass}>
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              {AUDIENCES.map((a) => (
                <SelectItem key={a} value={a} data-testid={`intake-audience-${a.toLowerCase().replace(/\s+/g, "-")}`}>
                  {a}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-sm font-semibold text-slate-200">
            Engagement interest
          </Label>
          <Select value={form.package_interest} onValueChange={(v) => update("package_interest")(v)}>
            <SelectTrigger data-testid="intake-select-package" className={fieldClass}>
              <SelectValue placeholder="Select engagement" />
            </SelectTrigger>
            <SelectContent>
              {PACKAGES.map((p) => (
                <SelectItem key={p} value={p} data-testid={`intake-package-${p.toLowerCase().replace(/\s+/g, "-")}`}>
                  {p}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-sm font-semibold text-slate-200">Timeline</Label>
          <Select value={form.timeline} onValueChange={(v) => update("timeline")(v)}>
            <SelectTrigger data-testid="intake-select-timeline" className={fieldClass}>
              <SelectValue placeholder="When do you need this?" />
            </SelectTrigger>
            <SelectContent>
              {TIMELINES.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-sm font-semibold text-slate-200">Budget range</Label>
          <Select value={form.budget_range} onValueChange={(v) => update("budget_range")(v)}>
            <SelectTrigger data-testid="intake-select-budget" className={fieldClass}>
              <SelectValue placeholder="Select budget" />
            </SelectTrigger>
            <SelectContent>
              {BUDGETS.map((b) => (
                <SelectItem key={b} value={b}>
                  {b}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="website" className="text-sm font-semibold text-slate-200">
            Website / Instagram / TikTok
          </Label>
          <Input
            id="website"
            data-testid="intake-input-website"
            value={form.website}
            onChange={update("website")}
            placeholder="https://"
            className={fieldClass}
          />
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="project_details" className="text-sm font-semibold text-slate-200">
            Tell us about your operation and what you want to systemize *
          </Label>
          <Textarea
            id="project_details"
            data-testid="intake-textarea-details"
            value={form.project_details}
            onChange={update("project_details")}
            rows={6}
            required
            placeholder="Briefly describe your brand, current bottleneck, audience, revenue surfaces, and what you want to automate."
            className="bg-white/[0.04] border-white/10 text-white placeholder:text-slate-500 rounded-xl focus-visible:ring-cyan-400/40"
          />
        </div>
      </div>

      <div className="flex items-start gap-3 mt-6">
        <Checkbox
          id="consent_marketing"
          data-testid="intake-checkbox-marketing"
          checked={form.consent_marketing}
          onCheckedChange={(v) => update("consent_marketing")(Boolean(v))}
          className="mt-1 border-white/20 data-[state=checked]:bg-cyan-400 data-[state=checked]:text-slate-950 data-[state=checked]:border-cyan-400"
        />
        <Label htmlFor="consent_marketing" className="text-sm text-slate-400 leading-relaxed cursor-pointer">
          I'd like to receive occasional emails with infrastructure insights from Brand-in-a-Box. You can unsubscribe at any time.
        </Label>
      </div>

      <div className="mt-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <p className="text-xs text-slate-500 max-w-md">
          By submitting, you agree to our{" "}
          <a className="underline hover:text-cyan-300" href="/terms-of-service">Terms</a> and{" "}
          <a className="underline hover:text-cyan-300" href="/privacy-policy">Privacy Policy</a>.
          No instant checkout. Every engagement is reviewed.
        </p>
        <Button
          type="submit"
          data-testid="intake-form-submit-button"
          disabled={submitting}
          className="rounded-full bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-bold px-8 h-12 glow-cyan transition-all"
        >
          {submitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…
            </>
          ) : (
            <>
              Request a System Build <Send className="ml-2 h-4 w-4" />
            </>
          )}
        </Button>
      </div>
    </form>
  );
}
